import { useEffect, useState } from 'react';
import type { ListaInvitaciones } from '../../api/operaciones';
import type { Invitacion } from '../../api/ops/equipo';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { conY, emailValido } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { PERMISOS, type ContextoEquipo } from './contexto';
import { diaYMes, enDias, textoDeError } from './textos';

/**
 * Invitaciones (1.9): Enviada, Caducada, Anulada y Aceptada, con quién la envió (`invited_by`, G179) y su plazo de 7 días
 * (G67). «Reenviar» una enviada o caducada es `resendInvitation` (G336, G362): la misma invitación, otra vez «Enviada»
 * con un enlace nuevo de 7 días, y el anterior deja de funcionar. Una anulada no se reenvía (409): se invita otra vez con
 * el mismo email y roles. «Revocar» la anula al momento. «Invitar» abre RV Panel con el email y los roles.
 */
const ESTADO: Record<Invitacion['status'], [EstadoChip, string] | null> = {
    sent: null,
    expired: ['problem', 'Caducada'],
    revoked: null,
    accepted: null,
};

export function Invitaciones({ c, cifra, version }: { c: ContextoEquipo; cifra: (n: number) => void; version: number }) {
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<ListaInvitaciones | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [ocupada, setOcupada] = useState<string | null>(null);
    const { ops, negocio, avisar } = c;
    const gestionar = c.permisos.puede(PERMISOS.gestionar) && !c.soloLectura;

    useEffect(() => {
        let vivo = true;
        ops.listInvitations(negocio, { cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                    cifra(l.total_count);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pag.cursor, intento, version, cifra]);

    if (error !== null && lista === null) {
        return <EstadoError seccion="Invitaciones" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={c.movil} />;
    }
    if (lista === null) {
        return null;
    }

    const despues = (texto: string) => {
        avisar({ tipo: 'ok', texto });
        setIntento((i) => i + 1);
        c.refrescar();
    };

    const revocar = (inv: Invitacion) => {
        setOcupada(inv.id);
        ops.revokeInvitation(negocio, inv.id)
            .then(() => {
                despues('Invitación revocada.');
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido revocar la invitación. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupada(null);
            });
    };

    const reenviar = (inv: Invitacion) => {
        setOcupada(inv.id);
        const envio = inv.status === 'sent' || inv.status === 'expired' ? ops.resendInvitation(negocio, inv.id) : ops.createInvitation(negocio, { email: inv.email, role_ids: inv.roles.map((r) => r.id) });
        envio
            .then(() => {
                despues(`Invitación reenviada a ${inv.email}.`);
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido reenviar la invitación. Prueba de nuevo.') });
                setIntento((i) => i + 1);
            })
            .finally(() => {
                setOcupada(null);
            });
    };

    const ahora = new Date();
    if (lista.data.length === 0) {
        return (
            <div className="rv-eq-vacia">
                <span className="rv-eq-vacia__baldosa">
                    <Icono nombre="inbox" />
                </span>
                <strong>No hay invitaciones.</strong>
            </div>
        );
    }

    return (
        <>
            <div className="rv-eq-lista" role="list" aria-label="Invitaciones">
                {lista.data.map((inv) => {
                    const chip = ESTADO[inv.status];
                    const plazo = enDias(inv.expires_at, ahora);
                    const enviada = enDias(inv.created_at, ahora);
                    const linea = [
                        // G995: varios roles con «y» antes del último («Encargado y Recepción»).
                        conY(inv.roles.map((r) => r.name)),
                        `Enviada${inv.invited_by === undefined ? '' : ` por ${inv.invited_by.name}`} ${Math.abs(enviada.dias) > 6 ? diaYMes(inv.created_at) : enviada.texto}`,
                        inv.status === 'sent' ? `Caduca ${plazo.texto}` : inv.status === 'expired' ? `Caducó ${plazo.texto}` : inv.status === 'revoked' ? 'Anulada' : 'Aceptada',
                    ]
                        .filter((t) => t !== '')
                        .join(' • ');
                    return (
                        <div key={inv.id} role="listitem" className="rv-eq-inv">
                            <span className="rv-eq-inv__baldosa">
                                <Icono nombre="mail" />
                            </span>
                            <span className="rv-eq-inv__texto">
                                <strong>{inv.email}</strong>
                                <span>{linea}</span>
                            </span>
                            <span className="rv-eq-inv__acciones">
                                {chip !== null && <Chip estado={chip[0]}>{chip[1]}</Chip>}
                                {gestionar && inv.status !== 'accepted' && (
                                    <Boton tipo="secundario" icono="rotate-ccw" ocupado={ocupada === inv.id} onClick={() => { reenviar(inv); }}>
                                        Reenviar
                                    </Boton>
                                )}
                                {gestionar && inv.status === 'sent' && (
                                    <Boton tipo="secundario" rojo icono="ban" disabled={ocupada === inv.id} onClick={() => { revocar(inv); }}>
                                        Revocar
                                    </Boton>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
            {(pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <div className="rv-eq-pie">
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={c.movil} />
                </div>
            )}
        </>
    );
}

/** «Invitar al equipo» (1.9): RV Panel con el email y uno o varios roles. */
export function PanelInvitar({ c, abierto, onCerrar, onEnviada }: { c: ContextoEquipo; abierto: boolean; onCerrar: () => void; onEnviada: () => void }) {
    const [email, setEmail] = useState('');
    const [roles, setRoles] = useState<string[]>([]);
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorEmail, setErrorEmail] = useState<string | null>(null);
    const { ops, negocio, avisar } = c;

    const errorLocal = intentado && !emailValido(email) ? 'Escribe un email válido.' : null;
    const sinRoles = intentado && roles.length === 0;

    const enviar = () => {
        setIntentado(true);
        setErrorEmail(null);
        if (!emailValido(email) || roles.length === 0) {
            return;
        }
        setOcupado(true);
        ops.createInvitation(negocio, { email: email.trim(), role_ids: roles })
            .then(() => {
                avisar({ tipo: 'ok', texto: `Invitación enviada a ${email.trim()}.` });
                setEmail('');
                setRoles([]);
                setIntentado(false);
                onEnviada();
            })
            .catch((e: unknown) => {
                setErrorEmail(textoDeError(e, 'No he podido enviar la invitación. Prueba de nuevo.'));
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={c.movil}
            icono="user-plus"
            eyebrow="Invitación"
            titulo="Invitar al equipo"
            subtitulo="Entra con su propia cuenta. El enlace caduca a los 7 días."
            accion="Enviar invitación"
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Enviando…"
            inactiva={c.soloLectura}
            onAccion={enviar}
        >
            <div className="rv-eq-ficha">
                <Campo etiqueta="Email" tipo="email" valor={email} onChange={setEmail} error={errorLocal ?? errorEmail} ayuda="Le llega un email con el enlace para unirse." autocompletar="off" nombre="email-invitado" onEnter={enviar} />
                <h3 className="rv-eq-subtitulo">Roles</h3>
                {c.roles.map((r) => (
                    <Interruptor
                        key={r.id}
                        etiqueta={r.name}
                        ayuda={r.description ?? (r.kind === 'custom' ? 'Rol propio de tu negocio.' : undefined)}
                        valor={roles.includes(r.id)}
                        onChange={(on) => {
                            setRoles((actual) => (on ? [...actual, r.id] : actual.filter((x) => x !== r.id)));
                        }}
                    />
                ))}
                <p className={`rv-eq-nota${sinRoles ? ' rv-eq-nota--error' : ''}`}>
                    {sinRoles ? 'Marca al menos un rol.' : 'Una persona puede tener varios roles. Suma los permisos de todos.'}
                </p>
            </div>
        </PanelLateral>
    );
}
